import React from 'react';
import { ChatProvider } from '../context/ChatContext';
import { createChatClient, API_BASE } from '../lib/chatApi';
import { SearchInput } from '../components/ChatInput';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { useLocale } from '../context/LocaleContext';
import { fetchWidgetInit, getBackendMode, getWidgetOrigins } from '../embed/widgetInit';
import {
  getCachedWidgetInit,
  getWidgetInitCacheKey,
  setCachedWidgetInit,
} from '../embed/widgetInitCache';

export default function SimpleSearchEntry() {
  const navigate = useNavigate();
  const { storeId } = useStore();
  const { t } = useLocale();

  const client = React.useMemo(
    () => createChatClient({ storeId, apiBase: API_BASE }),
    [storeId]
  );

  React.useEffect(() => {
    let active = true;
    const warmUp = async () => {
      const MODE = getBackendMode();
      const { initUrl } = getWidgetOrigins(MODE);
      const cacheKey = getWidgetInitCacheKey({ storeId, mode: MODE, initUrl });
      if (getCachedWidgetInit(cacheKey)) return;
      const refreshed = await fetchWidgetInit(storeId, MODE);
      if (!active || !refreshed) return;
      setCachedWidgetInit(cacheKey, refreshed.token, refreshed.apiBase);
    };
    warmUp();
    return () => {
      active = false;
    };
  }, [storeId]);

  const exampleQueries = ["Völkl för 180cm man", "Jacka för topptur", "Avancerad pistskida"];

  const handleSubmit = (value: string) => {
    const q = (value || '').trim();
    if (!q) return;
    navigate(`/simple-search?q=${encodeURIComponent(q)}`);
  };

  const handleClose = () => navigate('/');

  return (
    <ChatProvider client={client}>
      <div
        className="ww-browser-entry h-screen flex flex-col"
        style={{ backgroundColor: 'var(--color-background)' }}
      >
        {/* Top bar: close button */}
        <div className="flex items-center justify-end p-[12px]">
          <button
            type="button"
            className="ww-icon-btn w-[36px] h-[36px] flex items-center justify-center rounded-full border"
            aria-label={t("common.close")}
            onClick={handleClose}
            title={t("common.close")}
          >
            <X className="w-[24px] h-[24px]" />
          </button>
        </div>

        <div className="flex-1 p-6 flex flex-col items-center justify-center">
          <div className="w-[90%] max-w-[760px] mx-auto">
            <h1
              className="text-3xl md:text-4xl font-bold text-center mb-8"
              style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}
            >
              {t("search.free_title")}
            </h1>

            <div className="flex flex-wrap gap-[8px] mb-8 justify-center">
              {exampleQueries.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="px-[12px] py-[4px] rounded-full border text-[14px]"
                  onClick={() => handleSubmit(q)}
                  aria-label={`Exempel: ${q}`}
                >
                  {q}
                </button>
              ))}
            </div>

            <SearchInput
              placeholder={t("search.placeholder")}
              selectedOptions={[]}
              onRemoveOption={() => {}}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </div>
    </ChatProvider>
  );
}
